import { useState, useEffect } from 'react';

interface VersionInfo {
  commitHash: string;
  buildDate: string;
}

export const useVersionCheck = () => {
  const [hasUpdate, setHasUpdate] = useState(false);

  const currentVersion = __APP_VERSION__;

  const checkForUpdates = async () => {
    try {
      const response = await fetch(`/version.json?_=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) return;

      const remoteVersion: VersionInfo = await response.json();

      // Only reload if the deployed commit hash is different from the running one
      if (remoteVersion.commitHash !== currentVersion) {
        console.log('New version detected. Reloading...', {
          current: currentVersion,
          new: remoteVersion.commitHash,
        });

        setHasUpdate(true);

        setTimeout(async () => {
          // Unregister service workers and clear caches before reloading
          // so the browser fetches fresh assets instead of serving stale ones
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map(r => r.unregister()));
          }
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map(k => caches.delete(k)));
          }
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    }
  };

  useEffect(() => {
    // Check immediately on mount
    checkForUpdates();
    
    // Check every 10 minutes
    const interval = setInterval(checkForUpdates, 10 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return { hasUpdate };
};
